import { CSSProperties, forwardRef, useEffect, useImperativeHandle, useState } from "react";
import styled from "@emotion/styled";
import { GrSkeleton, palette, typography } from "@jnpmedi/grebob";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  Row,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import { InformationGuide, InformationGuideProps } from "@newComponents/layout/InformationGuide";
import { MvnMessage } from "@newLibs/utils";
import { baseZIndex } from "@assets/theme/baseDefine/baseZIndex";
import { DocsTableSizeType } from "./DocsTable";
import { useTableFilters } from "./hooks/useTableFilters";
import { DOCS_FOLDER_LANG } from "@maven-msg/lang/docs";

export type DocsTableRefProps = {
  handleResetRowSelection: () => void;
};

export type DocsTableContentsProps<T> = {
  columns: ColumnDef<T, keyof T>[];
  data?: T[];
  isDataLoading?: boolean;
  loadingSkeletonCount?: number;
  isDataRefetching?: boolean;
  isHiddenHeaderCase?: boolean;
  onSelectedRows?: (rows: T[]) => void;
  onTableRowClick?: (rowId: bigint) => void;
  enableRowSelection?: boolean | ((row: Row<T>) => boolean) | undefined;
  size?: DocsTableSizeType;
  hasActiveFilterCustomEmptyGuide?: Omit<InformationGuideProps, "style">;
  emptyGuide?: Omit<InformationGuideProps, "style">;
  excludeHasFilterKeys?: Array<keyof T>;
  className?: string;
  style?: CSSProperties;
};

const EMPTY_TABLE_DATA: any[] = [];

export const DocsTableContents = forwardRef(
  <T extends { id: bigint }>(
    {
      columns,
      data,
      isDataLoading = false,
      loadingSkeletonCount = 15,
      isDataRefetching = false,
      isHiddenHeaderCase,
      onSelectedRows,
      onTableRowClick,
      enableRowSelection,
      size = "medium",
      hasActiveFilterCustomEmptyGuide,
      emptyGuide,
      excludeHasFilterKeys,
      className,
      style,
    }: DocsTableContentsProps<T>,
    ref: React.Ref<DocsTableRefProps>
  ) => {
    const { hasActiveFilters } = useTableFilters(excludeHasFilterKeys);

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

    const hasData = !!data?.length;
    const table = useReactTable({
      columns,
      data: data || EMPTY_TABLE_DATA,
      state: {
        rowSelection,
        columnFilters,
      },
      enableRowSelection,
      manualPagination: true,
      filterFromLeafRows: true,
      onRowSelectionChange: setRowSelection,
      onColumnFiltersChange: setColumnFilters,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getExpandedRowModel: getExpandedRowModel(),
    });

    useEffect(() => {
      if (isDataRefetching) {
        table.resetRowSelection();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDataRefetching]);

    useEffect(() => {
      const selectedRows = Object.keys(rowSelection).map<T>((rowId) => table.getRow(rowId).original);

      onSelectedRows?.(selectedRows);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowSelection]);

    useImperativeHandle(ref, () => ({
      handleResetRowSelection: table.resetRowSelection,
    }));

    const emptyGuideProps = hasActiveFilters
      ? hasActiveFilterCustomEmptyGuide
        ? hasActiveFilterCustomEmptyGuide
        : {
            title: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_NO_FILTER_RESULT_TITLE),
            description: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_NO_FILTER_RESULT_DESCRIPTION),
          }
      : { ...emptyGuide };

    return (
      <TableContainer
        component={Paper}
        className={`DocsTable ${className ?? ""}`}
        sx={{
          position: "relative",
          boxShadow: "unset",
          width: "100%",
          height: "100%",
          ...style,
        }}
      >
        <Table style={{ width: "100%", height: "auto" }}>
          {!(isHiddenHeaderCase && (isDataLoading || !hasData)) && (
            <DocsTableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      colSpan={header.colSpan}
                      sx={{
                        padding: size === "small" ? "8px 16px" : "16px",
                        minWidth: header.getSize() || "auto",
                        width: header.getSize() || "auto",
                        border: "none",
                        ...typography["Content.Subtitle04"],
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </DocsTableHeader>
          )}

          <DocsTableBody>
            {isDataLoading
              ? Array.from({ length: loadingSkeletonCount }).map((_, index) => (
                  <DocsTableRow key={`skeleton-${index}`} size={size}>
                    {columns.map((_, index) => (
                      <DocsTableCell key={`skeleton-cell-${index}`}>
                        <GrSkeleton width="100%" height="18px" />
                      </DocsTableCell>
                    ))}
                  </DocsTableRow>
                ))
              : hasData &&
                table.getRowModel().rows.map((row) => (
                  <DocsTableRow
                    key={row.id}
                    selected={row.getIsSelected()}
                    onClick={() => {
                      onTableRowClick?.(row.original.id);
                    }}
                    size={size}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <DocsTableCell
                          key={cell.id}
                          width={cell.column.getSize()}
                          style={{ padding: cell.column.id === "contextMenu" ? "0 4px" : "0px 16px" }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </DocsTableCell>
                      );
                    })}
                  </DocsTableRow>
                ))}
          </DocsTableBody>
        </Table>

        {!isDataLoading && !hasData && (
          <InformationGuide
            style={{
              position: "absolute",
              inset: "50%",
            }}
            {...emptyGuideProps}
          />
        )}
      </TableContainer>
    );
  }
);

const DocsTableHeader = styled(TableHead)`
  overflow-x: auto;
  position: sticky;
  top: 0;
  z-index: ${baseZIndex.layout.docsTableHeader};
  background-color: ${palette["white"]};
  border-bottom: 1px solid ${palette["grey025"]};
`;

const DocsTableBody = styled(TableBody)`
  overflow: auto;
  height: 100%;
`;

const DocsTableRow = styled(TableRow)<{ size?: DocsTableSizeType }>`
  height: ${({ size }) => (size === "small" ? "36px" : "52px")};

  &.MuiTableRow-root:hover {
    background-color: ${palette["grey020"]};
  }

  &.Mui-selected {
    background-color: ${palette["blue015"]};
  }

  &.Mui-selected:hover {
    background-color: ${palette["blue015"]};
  }
`;

const DocsTableCell = styled(TableCell)`
  padding: 0px 16px;
  box-sizing: border-box;
  color: ${palette["grey090"]};
  border-bottom: 1px solid ${palette["grey020"]};

  .is-destroyed {
    color: ${palette["grey030"]};

    .GrIcon {
      opacity: 0.3;
    }
  }

  .link-text {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
      text-decoration-color: ${palette["linkText"]};
    }
  }
`;
